// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Divider,
  Grid,
  Group,
  Modal,
  NumberInput,
  Paper,
  Radio,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { createReference, getDisplayString, getReferenceString } from '@medplum/core';
import type {
  Condition,
  Encounter,
  MedicationRequest,
  Patient,
  Practitioner,
  Reference,
  Task,
} from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import {
  buildHcpcsCoding,
  buildMaskExtension,
  buildPressureExtension,
  DME_MASK_TYPE_LABELS,
  DME_MASK_TYPES,
  DME_TASK_BUSINESS_STATUS_URL,
  DME_TASK_BUSINESS_STATUSES,
  DME_TASK_CODE_SYSTEM,
  DME_TASK_VENDOR_URL,
  getDmeTaskDueDate,
  isDmeMaskType,
  readMaskFromMedicationRequest,
  readPressureFromMedicationRequest,
  SUBMIT_DME_ORDER_TASK_CODE,
  SUBMIT_DME_ORDER_TASK_DISPLAY,
} from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { showErrorNotification } from '../../utils/notifications';
import {
  buildPrescriptionOrderTransaction,
  findOpenDmeTaskForOrder,
  shouldGenerateReferralFormNow,
} from '../../utils/prescription-order-transaction';
import {
  assertTransactionSucceeded,
  findCurrentPrescriptionArtifacts,
  findTransactionResource,
  invalidateOrderTransactionCaches,
  renderPrescriptionPdfBinary,
} from '../../utils/prescription-pdf';
import {
  attachReferralDocumentToTask,
  createReferralDocument,
  isReferralMedicationRequest,
} from '../../utils/referral-pdf';
import { withTenantScope } from '../../utils/tenant';
import { ProviderSelect } from '../tasks/ProviderSelect';

const ORDER_TYPES = [
  'New PAP setup',
  'Pressure / setting change',
  'Replacement device',
  'Supplies only',
  'Mask change / fitting',
  'Oral appliance referral',
  'Positional therapy',
] as const;
type OrderType = (typeof ORDER_TYPES)[number];
const THERAPIES = [
  'APAP',
  'CPAP',
  'BiPAP / BPAP',
  'BiPAP ST',
  'ASV',
  'Oral appliance therapy',
  'Positional therapy',
  'Other',
];
const MASKS = DME_MASK_TYPES.map((value) => ({ value, label: DME_MASK_TYPE_LABELS[value] }));
const DIAGNOSES = [
  { value: 'G47.33', label: 'Obstructive sleep apnea (G47.33)' },
  { value: 'G47.10', label: 'Hypersomnia, unspecified (G47.10)' },
  { value: 'I10', label: 'Essential hypertension (I10)' },
  { value: 'E66.9', label: 'Obesity, unspecified (E66.9)' },
];
const EXTRAS = [
  'Extra mask cushion',
  'Second mask (backup)',
  'Chinstrap',
  'Heated tubing',
  'Travel/battery adapter',
  'Cleaning supplies',
  'SD card',
];
const DEVICE_PRESSURE_MIN = 4;
const DEVICE_PRESSURE_MAX = 20;
const DME_PENDING_DOCUMENTATION_REASON = 'Pending signed encounter documentation';

export function buildDiagnosisOptions(chartDiagnoses: Condition[]): { value: string; label: string }[] {
  const seenCodes = new Set<string>();
  const fromChart: { value: string; label: string }[] = [];
  for (const condition of chartDiagnoses) {
    for (const coding of condition.code?.coding ?? []) {
      if (!coding.code || seenCodes.has(coding.code)) {
        continue;
      }
      seenCodes.add(coding.code);
      fromChart.push({
        value: coding.code,
        label: `${coding.display ?? condition.code?.text ?? 'Diagnosis'} (${coding.code})`,
      });
    }
  }
  return [...fromChart, ...DIAGNOSES.filter((fallback) => !seenCodes.has(fallback.value))];
}

export interface PrescriptionFormValues {
  orderType: OrderType | '';
  vendor: string;
  vendorDetail: string;
  priority: 'Routine' | 'Urgent' | 'STAT';
  quantity: number | '';
  taskNotes: string;
  therapy: string;
  fixed: number | '';
  min: number | '';
  max: number | '';
  ipap: number | '';
  epap: number | '';
  backup: number | '';
  inspiratoryTime: number | '';
  riseTime: number | '';
  epapMin: number | '';
  epapMax: number | '';
  psMin: number | '';
  psMax: number | '';
  equipmentInstructions: string;
  ramp: string;
  epr: string;
  mask: string;
  maskSize: string;
  humidifier: boolean;
  standardSupplies: boolean;
  extras: string[];
  reason: string;
  reasonNote: string;
  referral: string;
  positionalDevice: string;
  diagnosis: string;
  lengthOfNeed: string;
  specialInstructions: string;
  attestation: boolean;
}

type Values = PrescriptionFormValues;

export function getTypeSpecificSettingsErrors(
  therapy: string,
  values: Pick<Values, 'backup' | 'inspiratoryTime' | 'riseTime' | 'epapMin' | 'psMin'>
): string[] {
  const errors: string[] = [];
  if (therapy === 'BiPAP ST') {
    if (typeof values.backup !== 'number') {
      errors.push('Backup rate is required for BiPAP ST.');
    }
    if (typeof values.inspiratoryTime !== 'number') {
      errors.push('Inspiratory time is required for BiPAP ST.');
    }
    if (typeof values.riseTime !== 'number') {
      errors.push('Rise time is required for BiPAP ST.');
    }
  }
  if (therapy === 'ASV') {
    if (typeof values.epapMin !== 'number' || typeof values.psMin !== 'number') {
      errors.push('ASV EPAP and PS minimums are required.');
    }
    if (typeof values.backup !== 'number') {
      errors.push('Backup rate is required for ASV.');
    }
  }
  return errors;
}

/** The DME task is owned by the provider placing the order unless they pick another individual provider. */
export function defaultTaskRecipient(profile: Practitioner | undefined): Reference<Practitioner> | undefined {
  return profile?.id ? { reference: getReferenceString(profile), display: getDisplayString(profile) } : undefined;
}

export function buildDmeTaskDescription(vendor: string, taskNotes: string): string {
  const instruction =
    vendor === 'Parachute'
      ? 'Enter signed DME order in Parachute. Parachute will text provider to countersign.'
      : 'Route signed DME prescription PDF to the selected vendor.';
  return taskNotes.trim() ? `${instruction} ${taskNotes.trim()}` : instruction;
}

export const initialPrescriptionValues: Values = {
  orderType: 'New PAP setup',
  vendor: 'Parachute',
  vendorDetail: '',
  priority: 'Routine',
  quantity: 1,
  taskNotes: '',
  therapy: 'APAP',
  fixed: '',
  min: '',
  max: '',
  ipap: '',
  epap: '',
  backup: '',
  inspiratoryTime: '',
  riseTime: '',
  epapMin: '',
  epapMax: '',
  psMin: '',
  psMax: '',
  equipmentInstructions: '',
  ramp: 'Auto',
  epr: '2',
  mask: 'Patient preference / DME to fit',
  maskSize: '',
  humidifier: true,
  standardSupplies: true,
  extras: [],
  reason: '',
  reasonNote: '',
  referral: '',
  positionalDevice: '',
  diagnosis: 'G47.33',
  lengthOfNeed: '99 months / lifetime',
  specialInstructions: '',
  attestation: false,
};
type Config = {
  therapy: 'required' | 'prefill' | 'locked';
  settings: boolean;
  comfort: boolean;
  mask: boolean;
  humidifier: boolean;
  supplies: boolean;
  extra: '' | 'reason' | 'replacement' | 'referral' | 'positional';
};
function config(type: OrderType | ''): Config {
  if (type === 'Pressure / setting change') {
    return {
      therapy: 'prefill',
      settings: true,
      comfort: true,
      mask: false,
      humidifier: false,
      supplies: false,
      extra: 'reason',
    };
  }
  if (type === 'Replacement device') {
    return {
      therapy: 'prefill',
      settings: true,
      comfort: true,
      mask: true,
      humidifier: true,
      supplies: true,
      extra: 'replacement',
    };
  }
  if (type === 'Supplies only') {
    return {
      therapy: 'prefill',
      settings: false,
      comfort: false,
      mask: false,
      humidifier: false,
      supplies: true,
      extra: '',
    };
  }
  if (type === 'Mask change / fitting') {
    return {
      therapy: 'prefill',
      settings: false,
      comfort: false,
      mask: true,
      humidifier: false,
      supplies: true,
      extra: 'reason',
    };
  }
  if (type === 'Oral appliance referral') {
    return {
      therapy: 'locked',
      settings: false,
      comfort: false,
      mask: false,
      humidifier: false,
      supplies: false,
      extra: 'referral',
    };
  }
  if (type === 'Positional therapy') {
    return {
      therapy: 'locked',
      settings: false,
      comfort: false,
      mask: false,
      humidifier: false,
      supplies: false,
      extra: 'positional',
    };
  }
  return {
    therapy: 'required',
    settings: true,
    comfort: true,
    mask: true,
    humidifier: true,
    supplies: true,
    extra: '',
  };
}

function resolvedTherapy(type: OrderType | '', configured: string, mode: Config['therapy']): string {
  if (mode !== 'locked') {
    return configured;
  }
  return type === 'Oral appliance referral' ? 'Oral appliance therapy' : 'Positional therapy';
}

function vendorNextStep(vendor: string): string {
  if (vendor === 'Parachute') {
    return 'Task created. Care manager enters this order in Parachute. Parachute will text the signing NP separately to countersign in its portal.';
  }
  if (vendor === 'Daybreak') {
    return 'Care manager faxes or emails the signed PDF to Daybreak. The PDF is the whole payload.';
  }
  return 'Care manager follows the vendor instructions recorded on this order.';
}

const BIPAP_THERAPIES = ['BiPAP / BPAP', 'BiPAP ST'];
/** Therapies whose form shows the free-text "Equipment instructions" field. */
const EQUIPMENT_INSTRUCTION_THERAPIES = ['Oral appliance therapy', 'Positional therapy', 'Other'];

export const DME_ORDER_IDENTIFIER_SYSTEM = 'https://polarissleep.com/fhir/dme-order-number';

/**
 * Builds the MedicationRequest note from the form. The PDF (`prescription-pdf.ts`) reads these lines back by
 * label, so labels here must match the ones the PDF looks up — covered by NewPrescriptionModal.test.tsx.
 */
export function buildPrescriptionNote(v: PrescriptionFormValues): string {
  const c = config(v.orderType);
  const therapy = resolvedTherapy(v.orderType, v.therapy, c.therapy);
  // Every line is only recorded when that field is visible on the form for the selected order type, therapy, and
  // vendor (see `config` and the form JSX), so the PDF never prints values that are hidden — e.g. left over from
  // switching order type / therapy / vendor, or form defaults.
  const bipap = c.settings && BIPAP_THERAPIES.includes(therapy);
  const bipapSt = c.settings && therapy === 'BiPAP ST';
  const asv = c.settings && therapy === 'ASV';
  const reasonShown = c.extra === 'reason' || c.extra === 'replacement';
  return [
    `Order type: ${v.orderType}`,
    `Vendor: ${v.vendor}${v.vendor === 'Other' && v.vendorDetail ? ` — ${v.vendorDetail}` : ''}`,
    `Quantity: ${v.quantity}`,
    `Therapy: ${therapy}`,
    c.settings && therapy === 'CPAP' && v.fixed !== '' ? `Fixed pressure: ${v.fixed} cm H₂O` : '',
    c.settings && therapy === 'APAP' && v.min !== '' && v.max !== '' ? `APAP range: ${v.min}–${v.max} cm H₂O` : '',
    bipap && v.ipap !== '' && v.epap !== '' ? `BiPAP: IPAP ${v.ipap} / EPAP ${v.epap} cm H₂O` : '',
    asv && v.epapMin !== '' && v.epapMax !== '' ? `ASV EPAP: ${v.epapMin}–${v.epapMax} cm H₂O` : '',
    asv && v.psMin !== '' && v.psMax !== '' ? `ASV PS: ${v.psMin}–${v.psMax} cm H₂O` : '',
    (bipapSt || asv) && v.backup !== '' ? `Backup rate: ${v.backup} bpm` : '',
    bipapSt && v.inspiratoryTime !== '' ? `Inspiratory time: ${v.inspiratoryTime} sec` : '',
    bipapSt && v.riseTime !== '' ? `Rise time: ${v.riseTime} sec` : '',
    c.comfort ? `Comfort: Ramp ${v.ramp}, EPR/Bi-Flex ${v.epr}` : '',
    c.mask && v.mask ? `Mask: ${v.mask}${v.maskSize ? `, ${v.maskSize}` : ''}` : '',
    c.humidifier ? `Humidifier: ${v.humidifier ? 'Heated humidifier included' : 'Not included'}` : '',
    reasonShown && v.reason
      ? `Reason: ${v.reason}${c.extra === 'reason' && v.reasonNote ? ` — ${v.reasonNote}` : ''}`
      : '',
    c.extra === 'referral' && v.referral ? `Referral target: ${v.referral}` : '',
    c.extra === 'positional' && v.positionalDevice ? `Positional device: ${v.positionalDevice}` : '',
    c.supplies && v.standardSupplies ? 'Supplies: Standard per payer replacement schedule' : '',
    c.supplies && v.extras.length ? `Additional supplies: ${v.extras.join(', ')}` : '',
    v.vendor === 'Parachute' && v.lengthOfNeed.trim() ? `Length of Need: ${v.lengthOfNeed.trim()}` : '',
    EQUIPMENT_INSTRUCTION_THERAPIES.includes(therapy) && v.equipmentInstructions
      ? `Equipment instructions: ${v.equipmentInstructions}`
      : '',
    v.specialInstructions ? `Special instructions: ${v.specialInstructions}` : '',
  ]
    .filter(Boolean)
    .join('\n');
}

/** Every label `buildPrescriptionNote` writes, plus labels written by orders created before SLE-584. */
const NOTE_LABELS = [
  'Order type',
  'Vendor',
  'Quantity',
  'Therapy',
  'Fixed pressure',
  'APAP range',
  'BiPAP',
  'ASV EPAP',
  'ASV PS',
  'Backup rate',
  'Inspiratory time',
  'Rise time',
  'Comfort',
  'Mask',
  'Humidifier',
  'Reason',
  'Referral target',
  'Positional device',
  'Supplies',
  'Standard supplies',
  'Additional supplies',
  'Length of Need',
  'Equipment instructions',
  'Special instructions',
] as const;
type NoteLabel = (typeof NOTE_LABELS)[number];

/**
 * Labels that may still start a line once the given free-text field has begun. `buildPrescriptionNote` writes
 * special instructions last and equipment instructions only before them, so free text in those fields that happens
 * to start with a label (e.g. "Mask: bring the old one back") stays part of the free text.
 */
const LABELS_AFTER_FREE_TEXT: Partial<Record<NoteLabel, readonly NoteLabel[]>> = {
  'Equipment instructions': ['Special instructions'],
  'Special instructions': [],
};

/**
 * Splits an order note into `label -> value`. Lines that do not start with a known label continue the previous
 * value, so multi-line free text (equipment / special instructions) survives.
 */
function parseNoteFields(text: string): Map<NoteLabel, string> {
  const fields = new Map<NoteLabel, string>();
  let current: NoteLabel | undefined;
  for (const line of text.split('\n')) {
    const allowed: readonly NoteLabel[] = (current && LABELS_AFTER_FREE_TEXT[current]) ?? NOTE_LABELS;
    const label = allowed.find((candidate) => line.startsWith(`${candidate}: `));
    if (label) {
      current = label;
      fields.set(label, line.slice(label.length + 2));
    } else if (current) {
      fields.set(current, `${fields.get(current) ?? ''}\n${line}`);
    }
  }
  return fields;
}

function splitOnce(value: string, separator: string): [string, string] {
  const index = value.indexOf(separator);
  return index === -1 ? [value, ''] : [value.slice(0, index), value.slice(index + separator.length)];
}

function firstNumber(value: string | undefined): number | '' {
  const match = value?.match(/\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : '';
}

function numberRange(value: string | undefined): [number | '', number | ''] {
  const match = value?.match(/(\d+(?:\.\d+)?)–(\d+(?:\.\d+)?)/);
  return match ? [Number(match[1]), Number(match[2])] : ['', ''];
}

const PRIORITIES: Record<string, PrescriptionFormValues['priority']> = {
  routine: 'Routine',
  urgent: 'Urgent',
  stat: 'STAT',
};

/**
 * Form values for amending an order: the inverse of `buildPrescriptionOrder` / `buildPrescriptionNote`, so that
 * re-signing an unchanged amendment reproduces the same note (covered by a round-trip test). Values the note does not
 * carry fall back to the order's codings / extensions, then to the form defaults. Orders written before SLE-584 use
 * a few older labels (e.g. `Standard supplies:`), which are read too.
 */
export function prescriptionFormValuesFromOrder(order: MedicationRequest): PrescriptionFormValues {
  const initial = initialPrescriptionValues;
  const fields = parseNoteFields((order.note ?? []).map((note) => note.text ?? '').join('\n'));
  const orderTypeText = fields.get('Order type');
  const orderType = ORDER_TYPES.includes(orderTypeText as OrderType) ? (orderTypeText as OrderType) : initial.orderType;
  const c = config(orderType);
  const vendorText = fields.get('Vendor');
  const [vendor, vendorDetail] = vendorText ? splitOnce(vendorText, ' — ') : [initial.vendor, ''];
  const therapy =
    fields.get('Therapy') ??
    order.medicationCodeableConcept?.coding?.find((coding) => coding.system === 'http://polarissleep.com/therapies')
      ?.display ??
    initial.therapy;

  const structuredPressure = readPressureFromMedicationRequest(order);
  const structuredBipap = structuredPressure && 'ipap' in structuredPressure ? structuredPressure : undefined;
  const bipap = fields.get('BiPAP')?.match(/IPAP (\d+(?:\.\d+)?) \/ EPAP (\d+(?:\.\d+)?)/);
  const [min, max] = numberRange(fields.get('APAP range'));
  const [epapMin, epapMax] = numberRange(fields.get('ASV EPAP'));
  const [psMin, psMax] = numberRange(fields.get('ASV PS'));

  const comfort = fields.get('Comfort')?.match(/^Ramp (.+), EPR\/Bi-Flex (.+)$/);
  const maskText = fields.get('Mask');
  const [maskFromNote, maskSize] = maskText ? splitOnce(maskText, ', ') : ['', ''];
  const mask = maskFromNote || readMaskFromMedicationRequest(order) || (c.mask ? '' : initial.mask);
  const humidifier = fields.get('Humidifier');
  const [reason, reasonNote] = splitOnce(fields.get('Reason') ?? '', ' — ');
  const extras = fields.get('Additional supplies');
  let standardSupplies = initial.standardSupplies;
  if (fields.has('Supplies') || fields.has('Standard supplies')) {
    standardSupplies = true;
  } else if (c.supplies) {
    standardSupplies = false;
  }
  const quantity = order.dispenseRequest?.quantity?.value ?? firstNumber(fields.get('Quantity'));

  return {
    ...initial,
    orderType,
    vendor,
    vendorDetail,
    priority: PRIORITIES[order.priority ?? ''] ?? initial.priority,
    quantity: quantity === '' ? initial.quantity : quantity,
    therapy,
    fixed:
      firstNumber(fields.get('Fixed pressure')) ||
      (structuredPressure && 'pressure' in structuredPressure ? structuredPressure.pressure : ''),
    min,
    max,
    ipap: bipap ? Number(bipap[1]) : (structuredBipap?.ipap ?? ''),
    epap: bipap ? Number(bipap[2]) : (structuredBipap?.epap ?? ''),
    backup: firstNumber(fields.get('Backup rate')),
    inspiratoryTime: firstNumber(fields.get('Inspiratory time')),
    riseTime: firstNumber(fields.get('Rise time')),
    epapMin,
    epapMax,
    psMin,
    psMax,
    equipmentInstructions: fields.get('Equipment instructions') ?? '',
    ramp: comfort?.[1] ?? initial.ramp,
    epr: comfort?.[2] ?? initial.epr,
    mask,
    maskSize,
    humidifier: humidifier === undefined ? initial.humidifier : humidifier === 'Heated humidifier included',
    standardSupplies,
    extras: extras ? extras.split(', ').filter(Boolean) : [],
    reason,
    reasonNote,
    referral: fields.get('Referral target') ?? '',
    positionalDevice: fields.get('Positional device') ?? '',
    diagnosis: order.reasonCode?.[0]?.coding?.[0]?.code ?? initial.diagnosis,
    // An order without a recorded length of need must not silently become "lifetime": leave it for the provider.
    lengthOfNeed: fields.get('Length of Need') ?? (vendor === 'Parachute' ? '' : initial.lengthOfNeed),
    specialInstructions: fields.get('Special instructions') ?? '',
  };
}

export interface BuildPrescriptionOrderContext {
  patient: Patient;
  encounter?: Encounter;
  profile?: Practitioner;
  authoredOn: string;
  /** Business identifier for a new order; printed as the PDF order number before the server assigns an id. */
  orderNumber?: string;
}

export function buildPrescriptionOrder(
  v: PrescriptionFormValues,
  { patient, encounter, profile, authoredOn, orderNumber }: BuildPrescriptionOrderContext
): MedicationRequest {
  const c = config(v.orderType);
  const therapy = resolvedTherapy(v.orderType, v.therapy, c.therapy);
  const code = therapy.toLowerCase().replaceAll(' ', '-').replaceAll('/', '-');
  const quantity = v.quantity;
  if (typeof quantity !== 'number') {
    throw new Error('Quantity is required.');
  }
  const coding: NonNullable<MedicationRequest['medicationCodeableConcept']>['coding'] = [
    { system: 'http://polarissleep.com/therapies', code, display: therapy },
  ];
  if (therapy === 'CPAP' || therapy === 'BiPAP / BPAP') {
    coding.push(buildHcpcsCoding(therapy === 'CPAP' ? 'cpap' : 'bipap'));
  }
  const extension: MedicationRequest['extension'] = [];
  // Coded pressure / mask follow the same visibility rules as the note: hidden sections are not ordered.
  if (c.settings && therapy === 'CPAP' && typeof v.fixed === 'number') {
    extension.push(buildPressureExtension('cpap', { pressure: v.fixed }));
  }
  if (
    c.settings &&
    ['BiPAP / BPAP', 'BiPAP ST'].includes(therapy) &&
    typeof v.ipap === 'number' &&
    typeof v.epap === 'number'
  ) {
    extension.push(buildPressureExtension('bipap', { ipap: v.ipap, epap: v.epap }));
  }
  if (c.mask && isDmeMaskType(v.mask)) {
    extension.push(buildMaskExtension(v.mask));
  }
  return withTenantScope<MedicationRequest>({
    resourceType: 'MedicationRequest',
    ...(orderNumber ? { identifier: [{ system: DME_ORDER_IDENTIFIER_SYSTEM, value: orderNumber }] } : {}),
    status: 'active',
    intent: 'order',
    priority: v.priority.toLowerCase() as MedicationRequest['priority'],
    subject: createReference(patient),
    ...(encounter ? { encounter: createReference(encounter) } : {}),
    requester: profile ? createReference(profile) : undefined,
    authoredOn,
    medicationCodeableConcept: { coding, text: `${v.orderType} · ${therapy}` },
    dispenseRequest: { quantity: { value: quantity, unit: 'each' } },
    reasonCode: [{ coding: [{ system: 'http://hl7.org/fhir/sid/icd-10-cm', code: v.diagnosis }] }],
    note: [{ text: buildPrescriptionNote(v) }],
    extension,
  });
}

export interface BuildDmeTaskContext {
  patient: Patient;
  encounter?: Encounter;
  profile?: Practitioner;
  owner?: Reference<Practitioner>;
  authoredOn: string;
}

/** The submit-dme-order Task for a newly signed order. `focus` and the PDF input are added by the transaction. */
export function buildNewDmeTask(
  v: PrescriptionFormValues,
  { patient, encounter, profile, owner, authoredOn }: BuildDmeTaskContext
): Task {
  const documentationComplete = encounter?.status === 'finished' || !encounter;
  return withTenantScope<Task>({
    resourceType: 'Task',
    status: documentationComplete ? 'ready' : 'on-hold',
    intent: 'order',
    priority: v.priority.toLowerCase() as Task['priority'],
    authoredOn,
    requester: profile ? createReference(profile) : undefined,
    owner,
    for: createReference(patient),
    encounter: encounter ? createReference(encounter) : undefined,
    performerType: [{ text: 'Care Coordinator' }],
    restriction: { period: { end: getDmeTaskDueDate(v.priority.toLowerCase()) } },
    ...(documentationComplete ? {} : { statusReason: { text: DME_PENDING_DOCUMENTATION_REASON } }),
    code: {
      coding: [
        {
          system: DME_TASK_CODE_SYSTEM,
          code: SUBMIT_DME_ORDER_TASK_CODE,
          display: SUBMIT_DME_ORDER_TASK_DISPLAY,
        },
      ],
      text: `Submit DME order for ${patient.name?.[0]?.family ?? 'patient'}`,
    },
    extension: [
      {
        url: DME_TASK_BUSINESS_STATUS_URL,
        valueCode: documentationComplete
          ? DME_TASK_BUSINESS_STATUSES.new
          : DME_TASK_BUSINESS_STATUSES.pendingDocumentation,
      },
      { url: DME_TASK_VENDOR_URL, valueString: v.vendor },
    ],
    description: buildDmeTaskDescription(v.vendor, v.taskNotes),
  });
}

export interface NewPrescriptionModalProps {
  opened: boolean;
  onClose: () => void;
  patient: Patient;
  encounter?: Encounter;
  onOrdered?: () => void;
  existingOrder?: MedicationRequest;
}

export function NewPrescriptionModal({
  opened,
  onClose,
  patient,
  encounter,
  onOrdered,
  existingOrder,
}: NewPrescriptionModalProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [softWarningsAcknowledged, setSoftWarningsAcknowledged] = useState(false);
  const [suppliesExpanded, setSuppliesExpanded] = useState(false);
  const [activeOrder, setActiveOrder] = useState<MedicationRequest>();
  const [duplicateOrder, setDuplicateOrder] = useState(false);
  const [pendingTherapy, setPendingTherapy] = useState<string>();
  const [chartDiagnoses, setChartDiagnoses] = useState<Condition[]>([]);
  const [signed, setSigned] = useState(false);
  const [taskCreated, setTaskCreated] = useState(false);
  const form = useForm<Values>({ initialValues: initialPrescriptionValues });
  // Kept outside the Mantine form: a nested Reference in Values makes form path types too complex for tsc.
  const [taskRecipient, setTaskRecipient] = useState<Reference<Practitioner>>();
  const taskRecipientUnset = !taskRecipient;
  useEffect(() => {
    if (opened && taskRecipientUnset && profile?.id) {
      setTaskRecipient(defaultTaskRecipient(profile));
    }
  }, [opened, profile, taskRecipientUnset]);
  const c = config(form.values.orderType);
  const therapy = resolvedTherapy(form.values.orderType, form.values.therapy, c.therapy);
  const npi = profile?.identifier?.find((id) => id.system?.toLowerCase().includes('npi'))?.value;
  const localNpiBypass = import.meta.env.DEV;
  const providerName =
    profile?.name?.[0]?.text ??
    (`${profile?.name?.[0]?.given?.join(' ') ?? ''} ${profile?.name?.[0]?.family ?? ''}`.trim() || 'Signing provider');
  const closeModal = (): void => {
    form.reset();
    setTaskRecipient(undefined);
    setStep(0);
    setSigned(false);
    setTaskCreated(false);
    onClose();
  };

  // The order an amendment form was prefilled from, so later re-runs (e.g. changing order type) keep the user's edits.
  const prefilledOrderIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!opened) {
      prefilledOrderIdRef.current = undefined;
    }
    if (!opened || !patient.id) {
      return;
    }
    const loadActiveOrder = async (): Promise<void> => {
      const [orders, conditions] = await Promise.all([
        medplum.searchResources(
          'MedicationRequest',
          `patient=Patient/${patient.id}&status=active&_sort=-authoredon&_count=100`,
          { cache: 'no-cache' }
        ),
        medplum.searchResources('Condition', `patient=Patient/${patient.id}&_sort=-recorded-date&_count=100`, {
          cache: 'no-cache',
        }),
      ]);
      setChartDiagnoses(conditions as Condition[]);
      const dmeOrders = (orders as MedicationRequest[]).filter((order) =>
        order.medicationCodeableConcept?.coding?.some((coding) => coding.system === 'http://polarissleep.com/therapies')
      );
      const latest = existingOrder ?? dmeOrders[0];
      setActiveOrder(latest);
      setDuplicateOrder(
        dmeOrders.some(
          (order) => order.authoredOn && Date.now() - new Date(order.authoredOn).getTime() <= 14 * 24 * 60 * 60 * 1000
        )
      );
      if (existingOrder) {
        // Amendments start from every field of the order being amended, since re-signing regenerates its PDF.
        if (prefilledOrderIdRef.current !== existingOrder.id) {
          prefilledOrderIdRef.current = existingOrder.id;
          form.setValues(prescriptionFormValuesFromOrder(existingOrder));
        }
        return;
      }
      // Only order types that modify the active device prefill from it; a new therapy (e.g. OAT alongside PAP)
      // starts blank. The chosen order type is never overwritten, since this effect re-runs when it changes.
      if (!latest || config(form.values.orderType).therapy !== 'prefill') {
        return;
      }
      const therapyFromOrder = latest.medicationCodeableConcept?.coding?.find(
        (coding) => coding.system === 'http://polarissleep.com/therapies'
      )?.display;
      const noteText = latest.note?.map((note) => note.text).join('\n') ?? '';
      const numberFrom = (pattern: RegExp): number | '' => {
        const match = noteText.match(pattern);
        return match ? Number(match[1]) : '';
      };
      if (therapyFromOrder) {
        form.setFieldValue('therapy', therapyFromOrder);
      }
      const range = noteText.match(/APAP range: ([\d.]+)–([\d.]+)/);
      const bipap = noteText.match(/BiPAP: IPAP ([\d.]+) \/ EPAP ([\d.]+)/);
      const asvEpap = noteText.match(/ASV EPAP: ([\d.]+)–([\d.]+)/);
      const asvPs = noteText.match(/ASV PS: ([\d.]+)–([\d.]+)/);
      const structuredPressure = readPressureFromMedicationRequest(latest);
      const structuredFixed =
        structuredPressure && 'pressure' in structuredPressure ? structuredPressure.pressure : undefined;
      const structuredBipap = structuredPressure && 'ipap' in structuredPressure ? structuredPressure : undefined;
      const vendor = noteText.match(/Vendor: ([^\n—]+)(?: — ([^\n]+))?/)?.[1]?.trim();
      const vendorDetail = noteText.match(/Vendor: [^\n—]+ — ([^\n]+)/)?.[1] ?? '';
      form.setValues({
        vendor: vendor ?? initialPrescriptionValues.vendor,
        vendorDetail,
        quantity: latest.dispenseRequest?.quantity?.value ?? initialPrescriptionValues.quantity,
        diagnosis: latest.reasonCode?.[0]?.coding?.[0]?.code ?? initialPrescriptionValues.diagnosis,
        fixed: numberFrom(/Fixed pressure: ([\d.]+)/) || structuredFixed || '',
        min: range ? Number(range[1]) : '',
        max: range ? Number(range[2]) : '',
        ipap: bipap ? Number(bipap[1]) : (structuredBipap?.ipap ?? ''),
        epap: bipap ? Number(bipap[2]) : (structuredBipap?.epap ?? ''),
        epapMin: asvEpap ? Number(asvEpap[1]) : '',
        epapMax: asvEpap ? Number(asvEpap[2]) : '',
        psMin: asvPs ? Number(asvPs[1]) : '',
        psMax: asvPs ? Number(asvPs[2]) : '',
        backup: numberFrom(/Backup rate: ([\d.]+)/),
        inspiratoryTime: numberFrom(/Inspiratory time: ([\d.]+)/),
        riseTime: numberFrom(/Rise time: ([\d.]+)/),
      });
    };
    loadActiveOrder().catch(() => undefined);
  }, [existingOrder, form.setFieldValue, form.setValues, form.values.orderType, medplum, opened, patient.id]);
  const diagnosisOptions = useMemo(() => {
    return buildDiagnosisOptions(chartDiagnoses);
  }, [chartDiagnoses]);
  const encounterDiagnosisOptions = useMemo(
    () => chartDiagnoses.filter((condition) => condition.encounter?.reference?.startsWith('Encounter/')),
    [chartDiagnoses]
  );
  const existingDiagnosisOptions = useMemo(
    () => chartDiagnoses.filter((condition) => !condition.encounter?.reference),
    [chartDiagnoses]
  );
  const currentApap = useMemo(() => {
    const match = activeOrder?.note
      ?.map((note) => note.text)
      .join('\n')
      .match(/APAP range: ([\d.]+)–([\d.]+)/);
    return match ? { min: Number(match[1]), max: Number(match[2]) } : undefined;
  }, [activeOrder]);
  const errors = useMemo(() => {
    const e: string[] = [];
    const v = form.values;
    if (!v.orderType) {
      e.push('Select an order type.');
    }
    if (!v.diagnosis) {
      e.push('Select a coded ICD-10 diagnosis.');
    }
    if (typeof v.quantity !== 'number' || v.quantity < 1 || !Number.isInteger(v.quantity)) {
      e.push('Quantity must be a whole number of at least 1.');
    }
    if (!taskRecipient?.reference) {
      e.push('Task recipient is required.');
    }
    if (!npi && !localNpiBypass) {
      e.push('Signing provider NPI is required.');
    }
    if (!profile || providerName === 'Signing provider') {
      e.push('Signing provider name is required.');
    }
    if (v.vendor === 'Other' && !v.vendorDetail.trim()) {
      e.push('Enter vendor details.');
    }
    if (v.vendor === 'Parachute' && !v.lengthOfNeed) {
      e.push('Length of need is required for Parachute.');
    }
    if (c.extra === 'reason' && !v.reason) {
      e.push('Select a reason for change.');
    }
    if (c.extra === 'replacement' && !v.reason) {
      e.push('Select a replacement reason.');
    }
    if (c.extra === 'referral' && !v.referral.trim()) {
      e.push('Enter the dental / OAT referral target.');
    }
    if (c.extra === 'positional' && !v.positionalDevice) {
      e.push('Select a positional device.');
    }
    if (
      c.settings &&
      therapy === 'CPAP' &&
      (typeof v.fixed !== 'number' || v.fixed < DEVICE_PRESSURE_MIN || v.fixed > DEVICE_PRESSURE_MAX)
    ) {
      e.push(`Fixed pressure must be between ${DEVICE_PRESSURE_MIN} and ${DEVICE_PRESSURE_MAX} cm H₂O.`);
    }
    if (
      c.settings &&
      therapy === 'APAP' &&
      (typeof v.min !== 'number' || typeof v.max !== 'number' || v.min >= v.max)
    ) {
      e.push('APAP minimum must be lower than maximum.');
    }
    if (
      c.settings &&
      therapy === 'APAP' &&
      ((typeof v.min === 'number' && v.min < DEVICE_PRESSURE_MIN) ||
        (typeof v.max === 'number' && v.max > DEVICE_PRESSURE_MAX))
    ) {
      e.push(
        `APAP settings must stay within ${DEVICE_PRESSURE_MIN}–${DEVICE_PRESSURE_MAX} cm H₂O for this device family.`
      );
    }
    if (
      c.settings &&
      ['BiPAP / BPAP', 'BiPAP ST'].includes(therapy) &&
      (typeof v.ipap !== 'number' || typeof v.epap !== 'number' || v.ipap <= v.epap)
    ) {
      e.push('IPAP must be greater than EPAP.');
    }
    if (
      c.settings &&
      ['BiPAP / BPAP', 'BiPAP ST'].includes(therapy) &&
      [v.ipap, v.epap].some(
        (value) => typeof value === 'number' && (value < DEVICE_PRESSURE_MIN || value > DEVICE_PRESSURE_MAX)
      )
    ) {
      e.push(
        `BiPAP pressures must stay within ${DEVICE_PRESSURE_MIN}–${DEVICE_PRESSURE_MAX} cm H₂O for this device family.`
      );
    }
    e.push(...getTypeSpecificSettingsErrors(therapy, v));
    if (
      c.settings &&
      therapy === 'ASV' &&
      (typeof v.epapMin !== 'number' ||
        typeof v.epapMax !== 'number' ||
        v.epapMin >= v.epapMax ||
        typeof v.psMin !== 'number' ||
        typeof v.psMax !== 'number' ||
        v.psMin >= v.psMax)
    ) {
      e.push('ASV minimum values must be lower than maximum values.');
    }
    if (
      c.settings &&
      therapy === 'ASV' &&
      [v.epapMin, v.epapMax, v.psMin, v.psMax].some(
        (value) => typeof value === 'number' && (value < DEVICE_PRESSURE_MIN || value > DEVICE_PRESSURE_MAX)
      )
    ) {
      e.push(
        `ASV pressure settings must stay within ${DEVICE_PRESSURE_MIN}–${DEVICE_PRESSURE_MAX} cm H₂O for this device family.`
      );
    }
    return e;
  }, [c, form.values, npi, profile, providerName, taskRecipient, therapy]);
  const softWarnings = useMemo(() => {
    const warnings: string[] = [];
    if (
      therapy === 'CPAP' &&
      typeof form.values.fixed === 'number' &&
      form.values.fixed > 15 &&
      !form.values.specialInstructions.trim()
    ) {
      warnings.push('Fixed pressure is above 15 cm H₂O. Add a clinical comment or confirm to continue.');
    }
    if (form.values.orderType === 'Pressure / setting change' || form.values.orderType === 'Replacement device') {
      const noteText = activeOrder?.note?.map((note) => note.text).join('\n') ?? '';
      const currentFixed = noteText.match(/Fixed pressure: ([\d.]+)/);
      const currentBipap = noteText.match(/BiPAP: IPAP ([\d.]+) \/ EPAP ([\d.]+)/);
      const currentAsvEpap = noteText.match(/ASV EPAP: ([\d.]+)–([\d.]+)/);
      const currentAsvPs = noteText.match(/ASV PS: ([\d.]+)–([\d.]+)/);
      const deltas: number[] = [];
      if (therapy === 'CPAP' && currentFixed && typeof form.values.fixed === 'number') {
        deltas.push(Math.abs(form.values.fixed - Number(currentFixed[1])));
      }
      if (therapy === 'APAP' && currentApap) {
        if (typeof form.values.min === 'number') {
          deltas.push(Math.abs(form.values.min - currentApap.min));
        }
        if (typeof form.values.max === 'number') {
          deltas.push(Math.abs(form.values.max - currentApap.max));
        }
      }
      if (['BiPAP / BPAP', 'BiPAP ST'].includes(therapy) && currentBipap) {
        if (typeof form.values.ipap === 'number') {
          deltas.push(Math.abs(form.values.ipap - Number(currentBipap[1])));
        }
        if (typeof form.values.epap === 'number') {
          deltas.push(Math.abs(form.values.epap - Number(currentBipap[2])));
        }
      }
      if (therapy === 'ASV') {
        if (currentAsvEpap && typeof form.values.epapMin === 'number') {
          deltas.push(Math.abs(form.values.epapMin - Number(currentAsvEpap[1])));
        }
        if (currentAsvEpap && typeof form.values.epapMax === 'number') {
          deltas.push(Math.abs(form.values.epapMax - Number(currentAsvEpap[2])));
        }
        if (currentAsvPs && typeof form.values.psMin === 'number') {
          deltas.push(Math.abs(form.values.psMin - Number(currentAsvPs[1])));
        }
        if (currentAsvPs && typeof form.values.psMax === 'number') {
          deltas.push(Math.abs(form.values.psMax - Number(currentAsvPs[2])));
        }
      }
      if (
        deltas.some((delta) => delta > 4) &&
        !form.values.specialInstructions.trim() &&
        !form.values.reasonNote.trim()
      ) {
        warnings.push(
          'Pressure changes by more than 4 cm H₂O from the active device. Add a reason note or confirm to continue.'
        );
      }
    }
    if (duplicateOrder) {
      warnings.push('A DME order for this patient was placed within the last 14 days. Confirm to continue.');
    }
    if (form.values.orderType === 'Supplies only' && !activeOrder) {
      warnings.push('Supplies-only order has no active device on file. Confirm to continue.');
    }
    return warnings;
  }, [activeOrder, currentApap, duplicateOrder, form.values, therapy]);
  const number = (name: keyof Values, label: string, unit = 'cm H₂O', required = true): JSX.Element => (
    <NumberInput
      label={label}
      rightSection={
        <Text size="xs" c="dimmed" mr="sm">
          {unit}
        </Text>
      }
      rightSectionWidth={unit.length > 5 ? 76 : 52}
      min={0}
      step={0.5}
      required={required}
      {...form.getInputProps(name)}
    />
  );
  const setType = (value: string | null): void => {
    const type = (value ?? '') as OrderType | '';
    form.setFieldValue('orderType', type);
    if (type === 'Oral appliance referral') {
      form.setFieldValue('therapy', 'Oral appliance therapy');
    }
    if (type === 'Positional therapy') {
      form.setFieldValue('therapy', 'Positional therapy');
    }
  };
  const clearSettings = (): void => {
    form.setValues({
      fixed: '',
      min: '',
      max: '',
      ipap: '',
      epap: '',
      backup: '',
      inspiratoryTime: '',
      riseTime: '',
      epapMin: '',
      epapMax: '',
      psMin: '',
      psMax: '',
    });
  };
  const setTherapy = (value: string | null): void => {
    if (!value || value === therapy) {
      return;
    }
    const hasSettings = Object.values(form.values).some((entry) => typeof entry === 'number');
    if (hasSettings) {
      setPendingTherapy(value);
      return;
    }
    form.setFieldValue('therapy', value);
  };
  const submit = async (): Promise<void> => {
    if (errors.length || !form.values.attestation) {
      return;
    }
    setSubmitting(true);
    try {
      if (!profile) {
        throw new Error('Signing provider profile is unavailable.');
      }
      const v = form.values;
      const signedAt = new Date().toISOString();
      const amendedOrderId = existingOrder?.id;
      const orderKey = crypto.randomUUID();
      const orderPayload = buildPrescriptionOrder(v, {
        patient,
        encounter,
        profile,
        authoredOn: existingOrder?.authoredOn ?? signedAt,
        orderNumber: amendedOrderId ? undefined : orderKey,
      });
      const order: MedicationRequest = amendedOrderId
        ? { ...existingOrder, ...orderPayload, id: amendedOrderId }
        : orderPayload;
      const isReferral = isReferralMedicationRequest(order);

      // Everything that can fail without side effects runs first: rendering + uploading the PDF Binary (an orphaned
      // Binary is harmless) and reading what the amendment supersedes. All FHIR resources are then written in a
      // single transaction, so a failure never leaves the old PDF retired or an order without its Task.
      const binary = isReferral
        ? undefined
        : await renderPrescriptionPdfBinary(medplum, patient, profile, order, signedAt);
      // Any amendment supersedes the order's current PDF (prescription or referral form) and revokes its
      // ServiceRequest, even when the amended order produces no prescription PDF (e.g. switched to a referral).
      const supersededArtifacts = amendedOrderId
        ? await findCurrentPrescriptionArtifacts(medplum, amendedOrderId)
        : undefined;
      const openTask = amendedOrderId ? await findOpenDmeTaskForOrder(medplum, amendedOrderId) : undefined;
      const transaction = buildPrescriptionOrderTransaction({
        patient,
        practitioner: profile,
        order,
        orderFullUrl: `urn:uuid:${orderKey}`,
        newTask: buildNewDmeTask(v, { patient, encounter, profile, owner: taskRecipient, authoredOn: signedAt }),
        signedAt,
        binary,
        supersededArtifacts,
        openTask,
        expectedOrderVersionId: existingOrder?.meta?.versionId,
      });
      const response = await medplum.executeBatch(transaction);
      invalidateOrderTransactionCaches(medplum, transaction, response);
      // A stale amendment (the order changed since this form loaded) fails here instead of reverting the order.
      assertTransactionSucceeded(response);
      const createdOrder = findTransactionResource<MedicationRequest>(response, 'MedicationRequest');
      if (!createdOrder?.id) {
        throw new Error('DME order was saved without a MedicationRequest ID.');
      }
      if (isReferral && (await shouldGenerateReferralFormNow(medplum, createdOrder))) {
        try {
          const referralDocument = await createReferralDocument(medplum, patient, profile, createdOrder);
          const orderTask = findTransactionResource<Task>(response, 'Task');
          if (orderTask?.id) {
            await medplum.updateResource(withTenantScope(attachReferralDocumentToTask(orderTask, referralDocument)));
          }
        } catch (error) {
          Sentry.captureException(error);
          notifications.show({
            color: 'yellow',
            message: 'Order signed and task created, but the referral PDF could not be generated.',
          });
        }
      }
      setTaskCreated(true);
      notifications.show({ color: 'green', message: 'DME prescription ordered' });
      onOrdered?.();
      setSigned(true);
    } catch (error) {
      Sentry.captureException(error);
      showErrorNotification(error);
    } finally {
      setSubmitting(false);
    }
  };
  const completeness = [
    { label: 'Patient identifier', complete: Boolean(patient.id) },
    { label: 'Order date', complete: true },
    { label: 'Item description', complete: Boolean(form.values.orderType && therapy) },
    { label: 'Quantity', complete: typeof form.values.quantity === 'number' && form.values.quantity >= 1 },
    { label: 'Coded diagnosis', complete: Boolean(form.values.diagnosis) },
    { label: 'Prescriber name', complete: Boolean(profile && providerName !== 'Signing provider') },
    { label: 'Prescriber NPI', complete: Boolean(npi || localNpiBypass) },
    { label: 'Signature attestation', complete: form.values.attestation },
    { label: 'Signature date', complete: true },
  ];
  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      title={signed ? 'Order signed' : 'Order New Therapy'}
      size={960}
      centered
      radius="md"
      styles={{
        root: {
          overflow: 'hidden',
        },
        inner: {
          overflow: 'hidden',
          padding: 12,
        },
        content: {
          maxWidth: 'calc(100vw - 24px)',
          maxHeight: 'calc(100vh - 24px)',
          height: 'min(96vh, 900px)',
          overflow: 'hidden',
          borderRadius: 8,
          boxShadow: '0 24px 60px rgba(35, 4, 21, 0.28)',
        },
        header: {
          padding: '18px 20px 10px',
          alignItems: 'center',
          borderBottom: '0',
        },
        title: {
          color: '#3a0929',
          fontSize: 21,
          fontWeight: 700,
        },
        close: {
          color: '#7f7880',
          width: 28,
          height: 28,
        },
        body: {
          padding: '0 20px 0',
          height: 'calc(100% - 62px)',
          position: 'relative',
          overflow: 'hidden',
          '& .mantine-Paper-root': {
            borderColor: '#d9d4d8',
            borderRadius: 5,
            boxShadow: 'none',
          },
          '& .mantine-Input-input': {
            minHeight: 36,
            borderColor: '#cfc9ce',
            borderRadius: 5,
            fontSize: 13,
          },
          '& .mantine-InputWrapper-label': {
            color: '#171217',
            fontSize: 12.5,
            fontWeight: 700,
            marginBottom: 6,
          },
          '& .mantine-Text-root': { fontSize: 12.5 },
          '& .mantine-Radio-root': {
            border: '1px solid #d9d4d8',
            borderRadius: 5,
            padding: '7px 11px',
          },
          '& .mantine-Radio-root:has(input:checked)': {
            borderColor: '#3a0929',
            boxShadow: '0 0 0 1px #3a0929 inset',
          },
          '& .mantine-Checkbox-root': {
            fontSize: 12.5,
          },
          '& .mantine-Group-root > .mantine-Checkbox-root': {
            border: '1px solid #ddd7dc',
            borderRadius: 5,
            padding: '6px 9px',
          },
        },
      }}
    >
      <ScrollArea.Autosize
        h="100%"
        mah="calc(96vh - 86px)"
        offsetScrollbars
        styles={{
          root: { height: '100%', position: 'relative' },
          viewport: { height: '100%' },
        }}
      >
        {signed ? (
          <Stack gap="md" py="lg">
            <Alert color="green" title="Order signed and task created">
              Signed PDF stored in the patient chart. Processing task created. Order is now immutable; corrections
              require an amended order.
            </Alert>
            <Paper withBorder p="md">
              <Title order={4}>Next steps</Title>
              <Text mt="sm">
                {taskCreated ? vendorNextStep(form.values.vendor) : 'Processing task status unavailable.'}
              </Text>
            </Paper>
            <Group justify="flex-end">
              <Button onClick={closeModal}>Done</Button>
            </Group>
          </Stack>
        ) : (
          <Stack gap="md" pb={80}>
            <Paper withBorder px="sm" py={8} radius="sm" bg="#fafafa">
              <Group justify="space-between">
                <Group gap="md" wrap="wrap">
                  <Text fw={700}>
                    {patient.name?.[0]?.given?.join(' ')} {patient.name?.[0]?.family}
                  </Text>
                  <Text size="sm" c="dimmed">
                    DOB <b>{patient.birthDate ?? '—'}</b>
                  </Text>
                  <Text size="sm" c="dimmed">
                    MRN{' '}
                    <span style={{ fontFamily: 'monospace' }}>
                      {patient.identifier?.find((id) => id.system?.includes('mrn'))?.value ?? patient.id ?? '—'}
                    </span>
                  </Text>
                </Group>
                <Text size="sm" fw={600} c="#3a0929">
                  {encounter
                    ? `▣ This encounter · ${encounter.period?.start ? new Date(encounter.period.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Current encounter'}`
                    : 'Standalone order'}
                </Text>
              </Group>
            </Paper>
            {step === 0 ? (
              <>
                <Paper withBorder p="md">
                  <Title order={4} mb="md">
                    Order
                  </Title>
                  <Grid gutter="md">
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Select
                        label="Order type"
                        required
                        data={ORDER_TYPES}
                        value={form.values.orderType}
                        onChange={setType}
                      />
                      <Text size="xs" c="dimmed" mt={6}>
                        Determines which sections apply below.
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Select
                        label="DME vendor"
                        data={['Parachute', 'Daybreak', 'Other']}
                        {...form.getInputProps('vendor')}
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <Radio.Group label="Priority" {...form.getInputProps('priority')}>
                        <Group mt="xs" gap="xs">
                          <Radio value="Routine" label="Routine" />
                          <Radio value="Urgent" label="Urgent" />
                          <Radio value="STAT" label="STAT" />
                        </Group>
                      </Radio.Group>
                    </Grid.Col>
                    {form.values.vendor === 'Other' && (
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <TextInput label="Vendor detail" required {...form.getInputProps('vendorDetail')} />
                      </Grid.Col>
                    )}
                  </Grid>
                  <Text size="xs" c="dimmed" mt="xs">
                    Care manager enters this order in the Parachute portal and attaches the signed PDF. Parachute texts
                    the signing NP to countersign. <b>Length of need is required for Parachute.</b>
                  </Text>
                </Paper>
                {(form.values.orderType === 'Pressure / setting change' ||
                  form.values.orderType === 'Replacement device') && (
                  <Paper withBorder p="md">
                    <Group justify="space-between" mb="md">
                      <Title order={4}>Current device → new order</Title>
                      <Badge variant="light">Current values read-only</Badge>
                    </Group>
                    <Grid>
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <Paper bg="gray.0" p="sm">
                          <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                            Current — from chart
                          </Text>
                          <Text mt="xs">
                            {activeOrder?.medicationCodeableConcept?.text ?? 'No active DME order found'}
                          </Text>
                          <Text size="sm" c="dimmed">
                            {activeOrder?.note?.[0]?.text?.split('\n').slice(0, 4).join(' · ') ??
                              'Enter current settings below.'}
                          </Text>
                        </Paper>
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <Paper p="sm" withBorder style={{ borderColor: 'var(--mantine-color-pink-7)' }}>
                          <Text size="xs" fw={700} tt="uppercase" c="pink.8">
                            New settings
                          </Text>
                          <Text mt="xs">Enter new values below</Text>
                          <Text size="sm" c="dimmed">
                            Changes require a reason and are reviewed before signing.
                          </Text>
                        </Paper>
                      </Grid.Col>
                    </Grid>
                  </Paper>
                )}
                {c.settings && (
                  <Paper withBorder p="md">
                    <Group justify="space-between" mb="md">
                      <Title order={4}>Therapy & Settings</Title>
                      {c.therapy === 'prefill' && <Badge variant="light">Prefilled from active device</Badge>}
                    </Group>
                    <Grid>
                      <Grid.Col span={12}>
                        <Select
                          label="Therapy type"
                          data={THERAPIES}
                          disabled={c.therapy === 'locked'}
                          value={therapy}
                          onChange={setTherapy}
                        />
                      </Grid.Col>
                      {therapy === 'CPAP' && (
                        <Grid.Col span={{ base: 12, sm: 6 }}>{number('fixed', 'Fixed pressure (cmH₂O)')}</Grid.Col>
                      )}
                      {therapy === 'APAP' && (
                        <>
                          <Grid.Col span={{ base: 12, sm: 6 }}>{number('min', 'Minimum pressure (cmH₂O)')}</Grid.Col>
                          <Grid.Col span={{ base: 12, sm: 6 }}>{number('max', 'Maximum pressure (cmH₂O)')}</Grid.Col>
                        </>
                      )}
                      {['BiPAP / BPAP', 'BiPAP ST'].includes(therapy) && (
                        <>
                          <Grid.Col span={{ base: 12, sm: 6 }}>{number('ipap', 'IPAP')}</Grid.Col>
                          <Grid.Col span={{ base: 12, sm: 6 }}>{number('epap', 'EPAP')}</Grid.Col>
                          <Grid.Col span={{ base: 12, sm: 6 }}>
                            <NumberInput
                              label="Pressure support"
                              value={
                                typeof form.values.ipap === 'number' && typeof form.values.epap === 'number'
                                  ? form.values.ipap - form.values.epap
                                  : ''
                              }
                              readOnly
                              rightSection={<Text size="xs">cm H₂O</Text>}
                            />
                          </Grid.Col>
                          {therapy === 'BiPAP ST' && (
                            <>
                              <Grid.Col span={{ base: 12, sm: 6 }}>{number('backup', 'Backup rate', 'bpm')}</Grid.Col>
                              <Grid.Col span={{ base: 12, sm: 6 }}>
                                {number('inspiratoryTime', 'Inspiratory time', 'sec')}
                              </Grid.Col>
                              <Grid.Col span={{ base: 12, sm: 6 }}>{number('riseTime', 'Rise time', 'sec')}</Grid.Col>
                            </>
                          )}
                        </>
                      )}
                      {therapy === 'ASV' && (
                        <>
                          {(['epapMin', 'epapMax', 'psMin', 'psMax'] as const).map((field) => (
                            <Grid.Col span={{ base: 12, sm: 6 }} key={field}>
                              {number(field, field.replace(/([A-Z])/g, ' $1'))}
                            </Grid.Col>
                          ))}
                          <Grid.Col span={{ base: 12, sm: 6 }}>{number('backup', 'Backup rate', 'bpm')}</Grid.Col>
                        </>
                      )}
                      {['Oral appliance therapy', 'Positional therapy', 'Other'].includes(therapy) && (
                        <Grid.Col span={12}>
                          <Textarea
                            label="Equipment instructions"
                            required
                            {...form.getInputProps('equipmentInstructions')}
                          />
                        </Grid.Col>
                      )}
                    </Grid>
                    <Text size="xs" c="dimmed" mt="md">
                      Pressure inputs use 0.5 increments. Changing therapy clears incompatible settings.
                    </Text>
                    {pendingTherapy && (
                      <Alert color="yellow" title="Switching therapy clears settings">
                        <Text size="sm">
                          {pendingTherapy} uses different settings fields than {therapy}.
                        </Text>
                        <Group mt="xs">
                          <Button
                            size="compact-sm"
                            onClick={() => {
                              clearSettings();
                              form.setFieldValue('therapy', pendingTherapy);
                              setPendingTherapy(undefined);
                            }}
                          >
                            Clear and switch
                          </Button>
                          <Button size="compact-sm" variant="subtle" onClick={() => setPendingTherapy(undefined)}>
                            Keep current therapy
                          </Button>
                        </Group>
                      </Alert>
                    )}
                  </Paper>
                )}
                {!c.settings && (
                  <Paper withBorder p="md">
                    <Group justify="space-between" mb="md">
                      <Title order={4}>Therapy</Title>
                      <Badge variant="light">
                        {c.therapy === 'locked' ? 'Locked by order type' : 'Prefilled from active device'}
                      </Badge>
                    </Group>
                    <Select label="Therapy type" data={THERAPIES} value={therapy} disabled />
                    {['Oral appliance therapy', 'Positional therapy', 'Other'].includes(therapy) && (
                      <Textarea
                        mt="md"
                        label="Equipment instructions"
                        required
                        {...form.getInputProps('equipmentInstructions')}
                      />
                    )}
                  </Paper>
                )}
                {c.extra === 'reason' && (
                  <Paper withBorder p="md">
                    <Title order={4} mb="md">
                      Reason for change
                    </Title>
                    <SimpleGrid cols={{ base: 1, sm: 2 }}>
                      <Select
                        label="Reason"
                        required
                        data={[
                          'Residual events on download',
                          'Pressure intolerance',
                          'Persistent symptoms',
                          'Weight change',
                          'Titration study result',
                          'Patient request',
                          'Other',
                        ]}
                        {...form.getInputProps('reason')}
                      />
                      <TextInput label="Note" {...form.getInputProps('reasonNote')} />
                    </SimpleGrid>
                  </Paper>
                )}
                {c.extra === 'replacement' && (
                  <Paper withBorder p="md">
                    <Title order={4} mb="md">
                      Replacement
                    </Title>
                    <Select
                      label="Replacement reason"
                      required
                      data={['Lost', 'Damaged', 'Stolen', 'End of useful lifetime (5 yr)', 'Other']}
                      {...form.getInputProps('reason')}
                    />
                  </Paper>
                )}
                {c.extra === 'referral' && (
                  <Paper withBorder p="md">
                    <Title order={4}>Referral target</Title>
                    <TextInput mt="md" label="Dental / OAT provider" required {...form.getInputProps('referral')} />
                  </Paper>
                )}
                {c.extra === 'positional' && (
                  <Paper withBorder p="md">
                    <Title order={4}>Positional device</Title>
                    <Select
                      mt="md"
                      label="Device type"
                      required
                      data={['Positional trainer (vibrotactile)', 'Positional belt', 'Other']}
                      {...form.getInputProps('positionalDevice')}
                    />
                  </Paper>
                )}
                {c.comfort && (
                  <Paper withBorder p="md">
                    <Group gap={6} mb="md">
                      <Title order={4}>Comfort</Title>
                      <Text size="sm" c="dimmed">
                        optional
                      </Text>
                    </Group>
                    <SimpleGrid cols={{ base: 1, sm: 2 }}>
                      <Select
                        label="Ramp"
                        data={['Off', 'Auto', '5 min', '10 min', '15 min', '20 min', '30 min', '45 min']}
                        {...form.getInputProps('ramp')}
                      />
                      <Select
                        label={therapy.startsWith('BiPAP') ? 'Bi-Flex' : 'EPR'}
                        data={['Off', '1', '2', '3']}
                        {...form.getInputProps('epr')}
                      />
                    </SimpleGrid>
                  </Paper>
                )}
                {(c.mask || c.humidifier || c.supplies) && (
                  <Paper withBorder p="md">
                    <Title order={4} mb="md">
                      Interface & Supplies
                    </Title>
                    {c.mask && (
                      <Grid gutter="sm">
                        <Grid.Col span={{ base: 12, sm: 6 }}>
                          <Select label="Mask type" required data={MASKS} {...form.getInputProps('mask')} />
                          <Text size="xs" c="dimmed" mt={6}>
                            Recommended — lets the DME fit the patient directly.
                          </Text>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 6 }}>
                          <TextInput label="Mask size" {...form.getInputProps('maskSize')} />
                        </Grid.Col>
                      </Grid>
                    )}
                    {c.humidifier && (
                      <Paper withBorder p="xs" mt="sm" radius="sm" style={{ borderColor: '#3a0929' }}>
                        <Checkbox
                          label="Heated humidifier"
                          {...form.getInputProps('humidifier', { type: 'checkbox' })}
                        />
                      </Paper>
                    )}
                    {c.supplies && (
                      <>
                        <Paper withBorder p="xs" mt="sm" radius="sm" style={{ borderColor: '#3a0929' }}>
                          <Checkbox
                            label="Standard supplies per payer replacement schedule"
                            {...form.getInputProps('standardSupplies', { type: 'checkbox' })}
                          />
                        </Paper>
                        {form.values.standardSupplies && (
                          <Button
                            variant="subtle"
                            size="compact-sm"
                            mt={4}
                            onClick={() => setSuppliesExpanded((value) => !value)}
                          >
                            {suppliesExpanded ? 'Hide replacement schedule' : 'Preview replacement schedule'}
                          </Button>
                        )}
                        {suppliesExpanded && form.values.standardSupplies && (
                          <Stack gap={4} mt="xs" pl="sm">
                            {[
                              'Mask cushion — every 1 month',
                              'Filter — every 2 weeks',
                              'Mask — every 3 months',
                              'Tubing — every 3 months',
                              'Humidifier chamber — every 6 months',
                            ].map((item) => (
                              <Text size="sm" c="dimmed" key={item}>
                                • {item}
                              </Text>
                            ))}
                          </Stack>
                        )}
                        <Text size="sm" fw={600} mt="md" mb="xs">
                          Additional or exception items{' '}
                          <Text component="span" size="xs" c="dimmed">
                            optional
                          </Text>
                        </Text>
                        <Group gap="xs">
                          {EXTRAS.map((extra) => (
                            <Checkbox
                              key={extra}
                              label={extra}
                              checked={form.values.extras.includes(extra)}
                              onChange={() =>
                                form.setFieldValue(
                                  'extras',
                                  form.values.extras.includes(extra)
                                    ? form.values.extras.filter((item) => item !== extra)
                                    : [...form.values.extras, extra]
                                )
                              }
                            />
                          ))}
                        </Group>
                      </>
                    )}
                  </Paper>
                )}
                <Paper withBorder p="md">
                  <Title order={4} mb="md">
                    Clinical
                  </Title>
                  <Select label="Diagnosis" required data={diagnosisOptions} {...form.getInputProps('diagnosis')} />
                  <Text size="xs" fw={600} mt="md">
                    This encounter
                  </Text>
                  <Stack gap={6}>
                    {(encounterDiagnosisOptions.length > 0 ? encounterDiagnosisOptions : chartDiagnoses).map(
                      (condition) =>
                        condition.code?.coding?.map((coding) => {
                          if (!coding.code) {
                            return null;
                          }
                          const checked = form.values.diagnosis === coding.code;
                          return (
                            <Checkbox
                              key={`encounter-${coding.code}`}
                              label={`${coding.display ?? condition.code?.text ?? 'Diagnosis'} ${coding.code}`}
                              checked={checked}
                              onChange={() => form.setFieldValue('diagnosis', coding.code ?? '')}
                            />
                          );
                        })
                    )}
                  </Stack>
                  {existingDiagnosisOptions.length > 0 && (
                    <>
                      <Text size="xs" fw={600} mt="md">
                        Existing conditions
                      </Text>
                      <Stack gap={6}>
                        {existingDiagnosisOptions.map((condition) =>
                          condition.code?.coding?.map((coding) => {
                            if (!coding.code) {
                              return null;
                            }
                            return (
                              <Checkbox
                                key={`existing-${coding.code}`}
                                label={`${coding.display ?? condition.code?.text ?? 'Condition'} ${coding.code}`}
                                checked={form.values.diagnosis === coding.code}
                                onChange={() => form.setFieldValue('diagnosis', coding.code ?? '')}
                              />
                            );
                          })
                        )}
                      </Stack>
                    </>
                  )}
                  <Text size="xs" c="dimmed" mt={6}>
                    Select a coded ICD-10 diagnosis. First selection is primary; free text is rejected by the vendor.
                  </Text>
                  <SimpleGrid cols={{ base: 1, sm: 2 }} mt="md">
                    {form.values.vendor === 'Parachute' && (
                      <Select
                        label="Length of need"
                        required
                        data={['3 months', '6 months', '9 months', '12 months', '99 months / lifetime']}
                        {...form.getInputProps('lengthOfNeed')}
                      />
                    )}
                    <NumberInput label="Quantity" min={1} step={1} required {...form.getInputProps('quantity')} />
                    <Textarea label="Special instructions" {...form.getInputProps('specialInstructions')} />
                  </SimpleGrid>
                  <Text size="xs" c="dimmed" mt={6}>
                    Required for Parachute.
                  </Text>
                </Paper>
                <Paper withBorder p="md">
                  <Title order={4} mb="md">
                    Task Notes
                  </Title>
                  <ProviderSelect
                    label="Task Recipient"
                    required
                    allowDeselect={false}
                    value={taskRecipient}
                    onChange={setTaskRecipient}
                  />
                  <Text size="xs" c="dimmed" mt={6}>
                    {existingOrder
                      ? "Amending keeps the owner of this order's open DME task; the recipient applies only if no open task exists."
                      : 'The provider who owns this task. Defaults to you, the ordering provider.'}
                  </Text>
                  <Textarea
                    mt="md"
                    label={
                      <>
                        Task Notes{' '}
                        <Text component="span" size="xs" c="dimmed">
                          internal
                        </Text>
                      </>
                    }
                    placeholder="Coordination notes for whoever works this task."
                    {...form.getInputProps('taskNotes')}
                  />
                  <Text size="xs" c="dimmed" mt={6}>
                    Posts to the Submit DME order task for the care team. Stays on the task — never printed on the
                    prescription.
                  </Text>
                </Paper>
                <Paper withBorder p="md">
                  <Title order={4} mb="md">
                    Ordering Provider
                  </Title>
                  <SimpleGrid cols={{ base: 1, sm: 2 }}>
                    <TextInput label="Provider Name" value={providerName} readOnly />
                    <TextInput label="NPI" value={npi ?? (localNpiBypass ? 'test bypass' : '')} readOnly />
                  </SimpleGrid>
                  <Checkbox
                    mt="md"
                    label="I certify that this equipment is medically necessary for this patient and that the information above is accurate."
                    {...form.getInputProps('attestation', { type: 'checkbox' })}
                  />
                  <Divider my="md" />
                  <Text size="xs" c="dimmed">
                    Signature is applied when the encounter is signed &amp; locked.
                  </Text>
                </Paper>
                {softWarnings.length > 0 && (
                  <Alert color="yellow" title="Review warning">
                    <Stack gap="xs">
                      {softWarnings.map((warning) => (
                        <Text size="sm" key={warning}>
                          {warning}
                        </Text>
                      ))}
                      <Checkbox
                        label="I reviewed this warning and want to continue."
                        checked={softWarningsAcknowledged}
                        onChange={(event) => setSoftWarningsAcknowledged(event.currentTarget.checked)}
                      />
                    </Stack>
                  </Alert>
                )}
                {errors.length > 0 && (
                  <Alert color="red" title="Before review">
                    <Stack gap={2}>
                      {errors.map((error) => (
                        <Text size="sm" key={error}>
                          {error}
                        </Text>
                      ))}
                    </Stack>
                  </Alert>
                )}
                <Group
                  justify="flex-end"
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 2,
                    margin: 0,
                    padding: '12px 20px',
                    borderTop: '1px solid #e3dfe3',
                    background: '#fff',
                  }}
                >
                  <Button variant="default" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button onClick={() => setStep(1)} disabled={softWarnings.length > 0 && !softWarningsAcknowledged}>
                    Order Therapy
                  </Button>
                </Group>
              </>
            ) : (
              <>
                <Paper withBorder p="md">
                  <Title order={4} mb="md">
                    Review order
                  </Title>
                  <Stack gap="xs">
                    <Text>
                      <b>Patient:</b> {patient.name?.[0]?.given?.join(' ')} {patient.name?.[0]?.family} · {patient.id}
                    </Text>
                    <Text>
                      <b>Order:</b> {form.values.orderType} · {form.values.vendor} · {form.values.priority}
                    </Text>
                    <Text>
                      <b>Therapy:</b> {therapy}
                    </Text>
                    {(c.settings || therapy === 'Other') && (
                      <Text>
                        <b>Settings:</b>{' '}
                        {therapy === 'CPAP' && (form.values.fixed === '' ? 'Not set' : `${form.values.fixed} cm H₂O`)}
                        {therapy === 'APAP' &&
                          (form.values.min === '' || form.values.max === ''
                            ? 'Not set'
                            : `${form.values.min}–${form.values.max} cm H₂O`)}
                        {['BiPAP / BPAP', 'BiPAP ST'].includes(therapy) &&
                          (form.values.ipap === '' || form.values.epap === ''
                            ? 'Not set'
                            : `IPAP ${form.values.ipap} / EPAP ${form.values.epap} cm H₂O`)}
                        {therapy === 'ASV' &&
                          (form.values.epapMin === '' || form.values.epapMax === ''
                            ? 'Not set'
                            : `EPAP ${form.values.epapMin}–${form.values.epapMax}, PS ${form.values.psMin}–${form.values.psMax} cm H₂O`)}
                        {['Other', 'Oral appliance therapy', 'Positional therapy'].includes(therapy) &&
                          (form.values.equipmentInstructions || 'Free-text equipment instructions not provided')}
                      </Text>
                    )}
                    {therapy === 'BiPAP ST' && (
                      <Text>
                        <b>BiPAP ST timing:</b> Backup {form.values.backup || 'Not set'} bpm · Inspiratory time{' '}
                        {form.values.inspiratoryTime || 'Not set'} sec · Rise time {form.values.riseTime || 'Not set'}{' '}
                        sec
                      </Text>
                    )}
                    {c.comfort && (
                      <Text>
                        <b>Comfort:</b> Ramp {form.values.ramp || 'Off'} · EPR/Bi-Flex {form.values.epr || 'Off'}
                      </Text>
                    )}
                    {c.mask && (
                      <Text>
                        <b>Interface:</b> {form.values.mask || 'Not selected'}
                        {form.values.maskSize ? ` · ${form.values.maskSize}` : ''}
                      </Text>
                    )}
                    {c.humidifier && (
                      <Text>
                        <b>Humidifier:</b> {form.values.humidifier ? 'Heated humidifier included' : 'Not included'}
                      </Text>
                    )}
                    {c.supplies && (
                      <Text>
                        <b>Supplies:</b>{' '}
                        {form.values.standardSupplies ? 'Payer replacement schedule' : 'No standard schedule'}
                        {form.values.extras.length ? ` · ${form.values.extras.join(', ')}` : ''}
                      </Text>
                    )}
                    {(c.extra === 'reason' || c.extra === 'replacement') && form.values.reason && (
                      <Text>
                        <b>Reason:</b> {form.values.reason}
                        {c.extra === 'reason' && form.values.reasonNote ? ` — ${form.values.reasonNote}` : ''}
                      </Text>
                    )}
                    {c.extra === 'referral' && form.values.referral && (
                      <Text>
                        <b>Referral target:</b> {form.values.referral}
                      </Text>
                    )}
                    {c.extra === 'positional' && form.values.positionalDevice && (
                      <Text>
                        <b>Positional device:</b> {form.values.positionalDevice}
                      </Text>
                    )}
                    {form.values.vendor === 'Parachute' && form.values.lengthOfNeed && (
                      <Text>
                        <b>Length of need:</b> {form.values.lengthOfNeed}
                      </Text>
                    )}
                    <Text>
                      <b>Diagnosis:</b> {form.values.diagnosis}
                    </Text>
                    <Divider my="xs" />
                    <Text size="sm">{form.values.specialInstructions || 'No special instructions'}</Text>
                  </Stack>
                </Paper>
                <Paper withBorder p="md">
                  <Title order={4} mb="md">
                    Completeness check
                  </Title>
                  <Stack gap="xs">
                    {completeness.map((item) => (
                      <Text key={item.label} c={item.complete ? 'green' : 'red'}>
                        {item.complete ? '✓' : '○'} {item.label}
                        {item.label === 'Prescriber NPI' && ` (${npi ?? (localNpiBypass ? 'test bypass' : 'missing')})`}
                        {item.label !== 'Prescriber NPI' && !item.complete && ' (missing)'}
                      </Text>
                    ))}
                  </Stack>
                </Paper>
                <Checkbox
                  label={`I certify this order is medically necessary and accurate. Signing as ${providerName}.`}
                  {...form.getInputProps('attestation', { type: 'checkbox' })}
                />
                <Alert color="blue">
                  Signing locks this order and generates its PDF. Corrections require an amended order.
                </Alert>
                <Group
                  justify="space-between"
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 2,
                    margin: 0,
                    padding: '12px 20px',
                    borderTop: '1px solid #e3dfe3',
                    background: '#fff',
                  }}
                >
                  <Button variant="default" onClick={() => setStep(0)}>
                    Back to order
                  </Button>
                  <Button
                    loading={submitting}
                    disabled={errors.length > 0 || !form.values.attestation}
                    onClick={submit}
                  >
                    Sign & order
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        )}
      </ScrollArea.Autosize>
    </Modal>
  );
}
